"use client";

/**
 * RBAC Permission hook.
 * Derives action-level permissions from the user's permission object and subscription state.
 */

import { useMemo } from "react";
import { UserRole, UserType, type IUserPermission } from "@speakai/shared";
import { useAuth } from "@/hooks/use-auth";
import { createDefaultPermission } from "@/config/permission-defaults";

// -- Types -----------------------------------------------------------

interface FolderPermissions {
  create?: boolean;
  delete?: boolean;
  download?: boolean;
  share?: boolean;
  assign?: boolean;
  accessAll?: boolean;
}

interface MediaPermissions {
  delete?: boolean;
  edit?: boolean;
  download?: boolean;
  share?: boolean;
}

interface RecorderPermissions {
  create?: boolean;
  edit?: boolean;
  delete?: boolean;
  download?: boolean;
  accessAll?: boolean;
}

interface PermissionMap {
  folder?: FolderPermissions;
  media?: MediaPermissions;
  recorder?: RecorderPermissions;
  teamManagement?: { manageMembers?: boolean; manageGroups?: boolean };
  developer?: { accessKeys?: boolean };
  payment?: { manageCards?: boolean; manageInvoices?: boolean };
  profileSettings?: {
    accountPreferences?: boolean;
    accountCustomization?: boolean;
    dataManagement?: boolean;
  };
  meetingAssistant?: {
    customizeAssistant?: boolean;
    shareMeetings?: boolean;
    routeMeetings?: boolean;
    excludeMeetings?: boolean;
    globalSettings?: boolean;
  };
  labels?: Partial<NonNullable<IUserPermission["labels"]>>;
  comments?: Partial<NonNullable<IUserPermission["comments"]>>;
  role?: UserRole;
}

export interface Permissions {
  // Subscription
  isPlanExpired: boolean;

  // Media
  canEditMedia: boolean;
  canDeleteMedia: boolean;
  canDownloadMedia: boolean;
  canShareMedia: boolean;
  canExportMedia: boolean;
  canMoveMedia: boolean;
  canReanalyzeMedia: boolean;

  // Folder
  canCreateFolder: boolean;
  canDeleteFolder: boolean;
  canDownloadFolder: boolean;
  canShareFolder: boolean;
  canAssignFolder: boolean;
  canAccessAllFolders: boolean;

  // Recorder
  canCreateRecorder: boolean;
  canEditRecorder: boolean;
  canDeleteRecorder: boolean;
  canDownloadRecorder: boolean;
  canAccessAllRecorders: boolean;

  canCreateLabels: boolean;
  canUpdateLabels: boolean;
  canArchiveLabels: boolean;
  canAssignLabels: boolean;
  canCreateComments: boolean;
  canResolveComments: boolean;
  canDeleteAnyComment: boolean;

  // Workspace
  isCompanyUser: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  isMember: boolean;
  /** True for owners/admins OR team-members granted billing access via team settings. */
  canManageBilling: boolean;
  /** True when user can manage team members (requires permission + collaboration feature). */
  canManageTeamMembers: boolean;
  /**
   * Company has the Speak Agents feature. Every agent-related surface — nav, routes, dashboard
   * templates, call-details, KB agent pickers — must gate on this; absent means off.
   */
  voiceAgentEnabled: boolean;
  /**
   * Company has the Knowledge Base feature. Voice agents are built on the knowledge base, so a
   * company with `voiceAgent.enabled` always has it; the reverse does not hold. Absent means off.
   */
  knowledgeBaseEnabled: boolean;

  /**
   * Low-level check: returns false if subscription is expired
   * or the specific permission flag is falsy.
   */
  canPerform: (
    category: keyof PermissionMap,
    action: string
  ) => boolean;
}

// -- Hook ------------------------------------------------------------

export function usePermissions(): Permissions {
  const { user } = useAuth();

  return useMemo(() => {
    const permission = (user?.permission ?? {}) as PermissionMap;
    const subscription = user?.subscription;
    const isPlanExpired = subscription?.isExpired ?? false;
    const features = subscription?.features ?? {};
    const userType = user?.userType ?? "";

    // Role helpers
    const role = permission.role;
    const isOwner = userType === UserType.Individual || role === UserRole.OWNER;
    const isAdmin = role === UserRole.ADMIN || isOwner;
    const isMember = role === UserRole.MEMBER;
    const canManageBilling =
      isAdmin ||
      !!permission.payment?.manageCards ||
      !!permission.payment?.manageInvoices;
    // Individual users always qualify; otherwise need both the explicit grant and the feature.
    const canManageTeamMembers =
      userType === UserType.Individual ||
      (!!permission.teamManagement?.manageMembers &&
        !!features.collaboration?.adminControl);
    const isCompanyUser =
      userType === UserType.Company &&
      !!features.collaboration?.adminControl;
    const voiceAgentEnabled = !!features.voiceAgent?.enabled;
    const knowledgeBaseEnabled = !!features.knowledgeBase?.enabled || voiceAgentEnabled;

    /**
     * Low-level permission check.
     * 1. If plan expired, deny everything (except payment.manageCards)
     * 2. Check the specific permission flag
     */
    function canPerform(
      category: keyof PermissionMap,
      action: string
    ): boolean {
      // Special case: payment.manageCards always allowed
      if (
        category === "payment" &&
        action === "manageCards" &&
        permission.payment?.manageCards
      ) {
        return true;
      }

      if (isPlanExpired) return false;

      const group = permission[category];
      if (!group) return true; // No restriction object = allowed
      const value = (group as Record<string, unknown>)[action];
      if (value === undefined) return true; // Field not present = allowed
      return !!value;
    }

    // -- Media permissions ---------------------------------------
    const canEditMedia = canPerform("media", "edit");
    const canDeleteMedia = canPerform("media", "delete");
    const canDownloadMedia = canPerform("media", "download");
    const canShareMedia = canPerform("media", "share");
    // Export requires both media.download permission AND subscription feature
    const canExportMedia =
      canDownloadMedia && !!(features as Record<string, Record<string, boolean>>).export?.bulk;
    // Move uses edit permission
    const canMoveMedia = canEditMedia;
    // Re-analyze uses edit permission
    const canReanalyzeMedia = canEditMedia;

    // -- Folder permissions --------------------------------------
    const canCreateFolder = canPerform("folder", "create");
    const canDeleteFolder = canPerform("folder", "delete");
    const canDownloadFolder = canPerform("folder", "download");
    const canShareFolder = canPerform("folder", "share");
    const canAssignFolder = canPerform("folder", "assign");
    const canAccessAllFolders = canPerform("folder", "accessAll");

    // -- Recorder permissions ------------------------------------
    const canCreateRecorder = canPerform("recorder", "create");
    const canEditRecorder = canPerform("recorder", "edit");
    const canDeleteRecorder = canPerform("recorder", "delete");
    const canDownloadRecorder = canPerform("recorder", "download");
    const canAccessAllRecorders = canPerform("recorder", "accessAll");

    // Owners pass every server check; a group missing from an older stored permission reads as the role default.
    const roleDefaults = createDefaultPermission(isAdmin ? UserRole.ADMIN : UserRole.MEMBER);
    const labelAction = (group: "labels" | "comments", action: string): boolean => {
      if (isPlanExpired) return false;
      if (isOwner) return true;
      const stored = permission[group] as Record<string, boolean | undefined> | undefined;
      return stored?.[action] ?? (roleDefaults[group] as Record<string, boolean>)[action] ?? false;
    };

    return {
      isPlanExpired,
      canCreateLabels: labelAction("labels", "create"),
      canUpdateLabels: labelAction("labels", "update"),
      canArchiveLabels: labelAction("labels", "delete"),
      canAssignLabels: labelAction("labels", "assign"),
      canCreateComments: labelAction("comments", "create"),
      canResolveComments: labelAction("comments", "update"),
      canDeleteAnyComment: labelAction("comments", "delete"),
      canEditMedia,
      canDeleteMedia,
      canDownloadMedia,
      canShareMedia,
      canExportMedia,
      canMoveMedia,
      canReanalyzeMedia,
      canCreateFolder,
      canDeleteFolder,
      canDownloadFolder,
      canShareFolder,
      canAssignFolder,
      canAccessAllFolders,
      canCreateRecorder,
      canEditRecorder,
      canDeleteRecorder,
      canDownloadRecorder,
      canAccessAllRecorders,
      isCompanyUser,
      isOwner,
      isAdmin,
      isMember,
      canManageBilling,
      canManageTeamMembers,
      voiceAgentEnabled,
      knowledgeBaseEnabled,
      canPerform,
    };
  }, [user]);
}
